import React, { useContext, useRef, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import FeedbackIcon from '@atlaskit/icon/core/feedback';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';
import UFOLabel from '@atlaskit/react-ufo/label';

import { CategorySelector } from '@confluence/category-selector';
import { DebouncedSearchBar } from '@confluence/debounced-search-bar';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { EditorContextPanelContext } from '@confluence/editor-context-panel/entry-points/EditorContextPanelContext.ts';
import { LazyGlobalFeedbackCollector } from '@confluence/global-feedback-collector/entry-points/GlobalFeedbackCollector.ts';
import { FeedbackSource } from '@confluence/global-feedback-collector/entry-points/feedbackSourceType.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { EMPTY_STATE_VARIANT, getEmptyStateVariant } from '../constants/emptyStateVariant.ts';
import {
	ANALYTICS_CREATE_SOURCE,
	ANALYTICS_ORIGIN,
	ANALYTICS_UI_SUBJECT_ID,
} from '../constants/snippetAnalytics.ts';
import { SNIPPET_CATEGORY_IDS } from '../constants/snippetCategories.ts';
import { useSnippetFilterCategories } from '../hooks/filters/useSnippetFilterCategories.ts';
import { EmptySnippets } from '../empty-state/EmptySnippets.tsx';
import { useSnippetAnalytics } from '../hooks/analytics/useSnippetAnalytics.ts';
import {
	filterSnippets,
	useSnippetFilterAndSort,
} from '../hooks/filters/useSnippetFilterAndSort.ts';
import { CreateSnippetModal } from '../modals/CreateSnippetModal.tsx';
import { useSnippets } from '../store/snippetsStore.ts';

import { SnippetPanelList } from './SnippetPanelList.tsx';
import { SnippetPopupPreviewProvider } from './preview/SnippetPopupPreviewContext.tsx';
import { SnippetSortDropdown } from './SnippetSortDropdown.tsx';
import { SnippetPanelSkeleton } from './skeleton/SnippetPanelSkeleton.tsx';

const messages = defineMessages({
	snippetsRecommended: {
		id: 'snippets.panel.recommended',
		defaultMessage: 'Recommended',
		description: 'Header for the list of Block Templates within the right panel',
	},
	searchSnippetsPlaceholderText: {
		id: 'snippets.panel.search.placeholderText',
		defaultMessage: 'Search author or topic',
		description: 'The placeholder text in the templates search box, visible when it is empty',
	},
	feedbackButtonLabel: {
		id: 'snippets.panel.feedback.button',
		defaultMessage: 'Give feedback',
		description:
			'Label for the button that opens the feedback collector modal from the Block Templates panel',
	},
});

const styles = cssMap({
	feedbackBar: {
		paddingBlock: token('space.150'),
		paddingInline: token('space.0'),
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
		backgroundColor: token('elevation.surface'),
	},
	searchBarWrapper: {
		height: '32px',
	},
	categoriesWrapper: {
		marginTop: token('space.0'),
		marginRight: token('space.050'),
		marginBottom: token('space.0'),
	},
});

type SnippetPanelProps = {
	contentId: string;
	spaceId: string;
	spaceKey: string;
};

export const SnippetPanel = ({ contentId, spaceId, spaceKey }: SnippetPanelProps) => {
	const intl = useIntl();
	const { userId } = useSessionData();
	const analyticsOrigin = ANALYTICS_ORIGIN.SNIPPET_PANEL;
	const { fireButtonClickedAnalytic, fireTextFieldTypedAnalytic } =
		useSnippetAnalytics(analyticsOrigin);
	const previousCategoryRef = useRef<string | null>(null);
	const [{ isLoading, snippets }] = useSnippets();

	const {
		selectedCategoryId,
		searchQuery,
		sortOrder,
		filteredSnippets,
		setSearchQuery,
		setCategoryFilter,
		setSortOrder,
	} = useSnippetFilterAndSort(snippets);
	const categories = useSnippetFilterCategories();

	const handleCategorySelected = (category: { id: string }) => {
		const previousCategoryId = previousCategoryRef.current;
		previousCategoryRef.current = category.id;

		fireButtonClickedAnalytic(ANALYTICS_UI_SUBJECT_ID.SNIPPET_CATEGORY_FILTER, {
			selectedCategoryId: category.id,
			...(previousCategoryId != null && { previousCategoryId }),
		});
		setCategoryFilter(category.id as SNIPPET_CATEGORY_IDS);
	};

	const handleSearch = (query: string) => {
		if (query.length > 0) {
			fireTextFieldTypedAnalytic(ANALYTICS_UI_SUBJECT_ID.SNIPPET_SEARCH, {
				searchQueryLength: query.length,
			});
		}
		setSearchQuery(query);
	};

	const [showFeedbackCollector, setShowFeedbackCollector] = useState(false);
	const { showModal } = useDialogs();
	const { editorAPI } = useContext(EditorContextPanelContext);

	const snippetsInCategoryOnly = filterSnippets(snippets, selectedCategoryId, '', userId);
	const isCategoryEmpty = snippetsInCategoryOnly.length === 0;
	const emptyStateVariant = getEmptyStateVariant(isCategoryEmpty, selectedCategoryId);

	if (isLoading) {
		return <SnippetPanelSkeleton />;
	}

	const emptyStatePrimaryActionMap: Partial<Record<EMPTY_STATE_VARIANT, () => void>> = {
		[EMPTY_STATE_VARIANT.FAVORITES]: () => setCategoryFilter(SNIPPET_CATEGORY_IDS.ALL),
		[EMPTY_STATE_VARIANT.MY_TEMPLATES]: () =>
			showModal(CreateSnippetModal, {
				contentId,
				spaceId,
				spaceKey,
				editorAPI,
				analyticsOrigin,
				createSource: ANALYTICS_CREATE_SOURCE.EMPTY_PANEL,
			}),
	};
	const emptyStatePrimaryAction = emptyStatePrimaryActionMap[emptyStateVariant];

	return (
		<Stack space="space.100" grow="fill">
			<Box xcss={styles.searchBarWrapper}>
				<DebouncedSearchBar
					placeholder={intl.formatMessage(messages.searchSnippetsPlaceholderText)}
					isCompact
					onChange={handleSearch}
					initialValue={searchQuery || ''}
				/>
			</Box>
			<Box xcss={styles.categoriesWrapper}>
				<CategorySelector
					categories={categories}
					selectedCategoryIds={[selectedCategoryId]}
					onCategorySelected={handleCategorySelected}
				/>
			</Box>
			<SnippetSortDropdown onChange={setSortOrder} value={sortOrder} />
			{filteredSnippets.length === 0 ? (
				<EmptySnippets
					origin={analyticsOrigin}
					variant={emptyStateVariant}
					primaryAction={emptyStatePrimaryAction}
				/>
			) : (
				<SnippetPopupPreviewProvider>
					<UFOLabel name="snippet-panel">
						<SnippetPanelList
							snippets={filteredSnippets}
							contentId={contentId}
							spaceId={spaceId}
							spaceKey={spaceKey}
						/>
					</UFOLabel>
				</SnippetPopupPreviewProvider>
			)}
			<Box xcss={styles.feedbackBar}>
				<Button
					iconBefore={FeedbackIcon}
					appearance="subtle"
					onClick={() => setShowFeedbackCollector(true)}
				>
					<FormattedMessage {...messages.feedbackButtonLabel} />
				</Button>
			</Box>
			{showFeedbackCollector && (
				<LazyGlobalFeedbackCollector
					feedbackSource={FeedbackSource.EDITOR}
					onClose={() => setShowFeedbackCollector(false)}
					onCloseComplete={() => {}}
				/>
			)}
		</Stack>
	);
};
